import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Package, BadgeCheck, Headset, Layers, Ruler, MapPin } from "lucide-react";
import Hero from "../components/Hero";
import Marquee from "../components/Marquee";
import ProductCard from "../components/ProductCard";
import Reviews from "../components/Reviews";
import { Reveal, FadeIn, EASE } from "../components/motion";
import { fetchProducts, gbp } from "../lib/shop";

const FAQS = [
  {
    q: "Where are you based?",
    a: "We are a UK-based brand, operating from London. All orders are processed and dispatched from within the UK.",
  },
  {
    q: "How long does shipping take?",
    a: "UK Standard Delivery: 2–5 working days. UK Express: 1–3 working days. Orders are typically dispatched within 24–48 hours. We also ship internationally — Europe 5–10 working days, Rest of World 7–14 working days.",
  },
  {
    q: "What is your returns policy?",
    a: "If you're not completely satisfied, you can return items within 14 days of receiving your order. Items must be unworn, unwashed and in their original condition. Contact info@01london.co.uk to start a return.",
  },
  {
    q: "What payment methods do you accept?",
    a: "All major debit and credit cards, plus secure checkout options including PayPal, Klarna, Apple Pay and Google Pay.",
  },
  {
    q: "How do the pieces fit?",
    a: "Our hoodies and tees are cut oversized and boxy with drop shoulders — true to size for a relaxed fit. If you're between sizes and want a more relaxed drape, size up. Full measurement tables are on every product page.",
  },
];

function TrustStrip() {
  const items = [
    { icon: ShieldCheck, label: "Secure checkout" },
    { icon: Package, label: "Dispatched within 48 hours" },
    { icon: BadgeCheck, label: "Premium heavyweight material" },
    { icon: Headset, label: "Dedicated customer support" },
  ];
  return (
    <div className="border-b border-white/10 bg-ink">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-2 lg:grid-cols-4">
        {items.map((it, i) => (
          <FadeIn key={it.label} delay={i * 0.06} className={`flex items-center gap-3 py-5 md:py-6 px-2 ${i !== 0 ? "lg:border-l" : ""} border-white/10 ${i % 2 === 1 ? "border-l" : ""} ${i > 1 ? "border-t lg:border-t-0" : ""}`}>
            <it.icon className="w-4 h-4 text-ash shrink-0" strokeWidth={1.5} />
            <span className="font-mono text-[10px] md:text-[11px] tracking-[0.14em] uppercase text-bone/80">{it.label}</span>
          </FadeIn>
        ))}
      </div>
    </div>
  );
}

function Benefits() {
  const items = [
    {
      icon: Layers,
      title: "Heavyweight quality",
      copy: "350 GSM heavyweight cotton in the Signature Hoodie — structured, substantial, built to hold its shape.",
    },
    {
      icon: Ruler,
      title: "Relaxed oversized fit",
      copy: "Boxy cut with drop shoulders. True to size for the intended drape — size up for a baggier fit.",
    },
    {
      icon: MapPin,
      title: "Minimal London streetwear",
      copy: "Distinctive design without the noise. Exclusively made in London.",
    },
  ];
  return (
    <section className="border-b border-white/10 bg-ink" data-testid="home-benefits">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid md:grid-cols-3 gap-px bg-white/10">
        {items.map((it, i) => (
          <FadeIn key={it.title} delay={i * 0.07} className="bg-ink flex flex-col gap-3 py-10 px-6 md:py-14 md:px-10">
            <it.icon className="w-5 h-5 text-ember shrink-0" strokeWidth={1.5} />
            <h3 className="font-display font-bold uppercase tracking-tight text-base md:text-lg">{it.title}</h3>
            <p className="text-ash text-sm leading-relaxed max-w-xs">{it.copy}</p>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

function Spotlight({ product, secondary }) {
  const [spotAspect, setSpotAspect] = useState(null);
  if (!product) return null;

  return (
    <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-36">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
        <Reveal className="relative order-2 lg:order-1 min-w-0">
          <div
            className="relative overflow-hidden clip-frame aspect-[4/5] bg-card"
            style={spotAspect ? { aspectRatio: String(spotAspect) } : undefined}
          >
            <img
              src={product.images[2] || product.images[0]}
              alt={product.title}
              className="absolute inset-0 w-full h-full object-cover"
              onLoad={(e) => setSpotAspect(e.target.naturalWidth / e.target.naturalHeight)}
            />
          </div>
          <div className="absolute -bottom-5 -right-3 md:right-8 bg-ember text-white px-5 py-4" data-testid="spotlight-stock-badge">
            <p className="font-mono text-[9px] tracking-[0.2em] uppercase opacity-80">Live availability</p>
            <p className="font-display font-extrabold text-xl uppercase tracking-tight">{product.badge || "IN STOCK"}</p>
          </div>
        </Reveal>

        <div className="order-1 lg:order-2 min-w-0">
          <Reveal>
            <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-ash uppercase mb-4">Featured piece</p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              {product.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-ash text-sm md:text-base leading-relaxed mt-7 max-w-md">
              {product.description[0]}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-9 flex flex-wrap items-center gap-6">
            <span className="font-display font-extrabold text-3xl" data-testid="spotlight-price">{gbp(product.price)}</span>
            <Link
              to={`/product/${product.handle}`}
              className="group inline-flex items-center gap-3 bg-bone text-ink font-mono text-xs tracking-[0.2em] px-8 py-4 hover:bg-ember hover:text-white transition-colors duration-400"
              data-testid="spotlight-cta-button"
            >
              SHOP THE ICON HOODIE
              <ArrowRight className="w-4 h-4 transition-transform duration-400 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-wrap gap-2.5 mt-8" data-testid="spotlight-specs">
              {product.details.slice(0, 4).map((d) => (
                <span key={d} className="font-mono text-[9px] md:text-[10px] tracking-[0.12em] uppercase border border-white/15 px-3 py-2 text-bone/80">
                  {d}
                </span>
              ))}
            </div>
          </Reveal>
          {secondary && (
            <Reveal delay={0.25}>
              <Link
                to={`/product/${secondary.handle}`}
                className="group mt-8 flex items-center gap-4 border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-colors duration-300 p-3 pr-5 max-w-md"
                data-testid="spotlight-secondary-card"
              >
                <div className="w-16 h-16 shrink-0 overflow-hidden bg-card">
                  <img src={secondary.images[0]} alt={secondary.title} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[9px] tracking-[0.2em] text-ash uppercase">Also in the drop</p>
                  <p className="font-display font-bold text-sm uppercase tracking-tight leading-snug mt-0.5">{secondary.title}</p>
                </div>
                <span className="font-mono text-sm shrink-0">{gbp(secondary.price)}</span>
                <ArrowRight className="w-4 h-4 text-ash group-hover:text-bone group-hover:translate-x-1 transition-all duration-300 shrink-0" />
              </Link>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}

function Story() {
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="story" ref={ref} className="border-y border-white/10 bg-coal/50">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-36 grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
        <div>
          <Reveal>
            <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-ash uppercase mb-4">Our story</p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-3xl sm:text-4xl xl:text-5xl">
              BUILT FROM FRUSTRATION.<br />
              <span className="text-outline">DRIVEN BY PURPOSE.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="text-ash text-sm md:text-base leading-relaxed mt-8 space-y-5 max-w-lg">
              <p>
                01 started from a simple frustration: too many brands creating noise instead of meaning — loud logos, fast-changing trends, products designed to be replaced rather than valued.
              </p>
              <p>
                <span className="text-bone font-semibold">Why "01"?</span> The beginning. The first step. Something built with intention rather than borrowed from what already exists.
              </p>
              <p className="text-bone/90 italic border-l-2 border-ember pl-5">
                "True style isn't about standing out for a moment — it's about knowing who you are from day one."
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/shop" className="group inline-flex items-center gap-3 mt-10 font-mono text-xs tracking-[0.2em] border border-white/20 hover:border-bone px-8 py-4 hover:bg-bone hover:text-ink transition-all duration-400" data-testid="story-cta-button">
              SHOP THE COLLECTION
              <ArrowRight className="w-4 h-4 transition-transform duration-400 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
        <Reveal className="relative">
          <div className="relative overflow-hidden clip-frame aspect-[4/5] bg-card max-w-md ml-auto w-full">
            <motion.img
              src="https://cdn.shopify.com/s/files/1/0927/6650/3246/files/IMG_0289_1280x.jpg?v=15412533418786344236"
              alt="01 London craftsmanship"
              style={{ y }}
              className="absolute inset-0 w-full h-full object-cover scale-110"
            />
          </div>
          <p className="font-mono text-[10px] tracking-[0.25em] text-ash uppercase mt-4 text-right">Made in London — 01</p>
        </Reveal>
      </div>
    </section>
  );
}

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-36">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-ash uppercase mb-4">Customer care</p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-3xl sm:text-5xl lg:text-4xl">
              EVERYTHING YOU<br />NEED TO KNOW
            </h2>
            <p className="text-ash text-sm leading-relaxed mt-6 max-w-xs">
              Sizing, shipping, and returns — all in one place. Still stuck?{" "}
              <a href="mailto:info@01london.co.uk" className="text-bone border-b border-white/30 hover:border-ember hover:text-ember transition-colors">Email us</a>.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          {FAQS.map((f, i) => (
            <Reveal key={i} delay={i * 0.04}>
              <div className="border-b border-white/10 first:border-t" data-testid={`faq-accordion-item-${i}`}>
                <button
                  onClick={() => setOpen(open === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-6 py-6 text-left group"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-display font-bold uppercase tracking-tight text-base md:text-lg group-hover:text-ash transition-colors">{f.q}</span>
                  <span className={`font-mono text-xl text-ash transition-transform duration-400 ${open === i ? "rotate-45" : ""}`}>+</span>
                </button>
                <motion.div
                  initial={false}
                  animate={{ height: open === i ? "auto" : 0, opacity: open === i ? 1 : 0 }}
                  transition={{ duration: 0.45, ease: EASE }}
                  className="overflow-hidden"
                >
                  <p className="text-ash text-sm leading-relaxed pb-7 pr-8 max-w-2xl">{f.a}</p>
                </motion.div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="relative border-t border-white/10 overflow-hidden">
      <div className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-36 text-center relative z-10">
        <Reveal>
          <h2 className="font-display font-extrabold uppercase tracking-tighter leading-[0.92] text-[9.5vw] sm:text-6xl lg:text-8xl">
            YOUR NEXT<br />
            <span className="text-outline">STATEMENT PIECE</span><br />
            AWAITS.
          </h2>
          <p className="font-mono text-[10px] md:text-xs tracking-[0.25em] text-ash uppercase mt-8">
            Free UK delivery over £75 — dispatched within 48 hours.
          </p>
          <Link
            to="/shop"
            className="group inline-flex items-center gap-3 bg-bone text-ink font-mono text-xs tracking-[0.2em] px-10 py-5 mt-10 hover:bg-ember hover:text-white transition-colors duration-400"
            data-testid="final-cta-button"
          >
            SHOP THE COLLECTION
            <ArrowRight className="w-4 h-4 transition-transform duration-400 group-hover:translate-x-1.5" />
          </Link>
        </Reveal>
      </div>
      <p className="pointer-events-none select-none absolute inset-x-0 -bottom-8 md:-bottom-14 text-center font-display font-extrabold uppercase text-[22vw] leading-none text-white/[0.03]">01 LONDON</p>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => {});
  }, []);

  const iconHoodie = products.find((p) => p.handle === "01-icon-oversized-hoodie") || products[0];
  const grid = products.slice(0, 6);

  return (
    <>
      <Hero product={iconHoodie} />
      <Marquee />
      <Benefits />
      <section className="max-w-[1500px] mx-auto px-5 md:px-10 py-24 md:py-36">
        <Reveal className="flex items-end justify-between gap-6 mb-14">
          <div>
            <p className="font-mono text-[10px] md:text-xs tracking-[0.3em] text-ash uppercase mb-4">The range</p>
            <h2 className="font-display font-extrabold uppercase tracking-tighter leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">FIND YOUR FIT</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] border border-white/20 hover:border-bone hover:bg-bone hover:text-ink px-6 py-3.5 transition-all duration-300" data-testid="home-shop-all-link">
            SHOP ALL <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-12 md:gap-x-6">
          {grid.length > 0
            ? grid.map((p, i) => <ProductCard key={p.handle} product={p} index={i} />)
            : [...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-card animate-pulse" />)}
        </div>
      </section>
      <TrustStrip />
      <Spotlight product={iconHoodie} secondary={products.find((p) => p.handle === "01-premium-oversized-hoodie")} />
      <Story />
      <Reviews />
      <FAQ />
      <FinalCTA />
    </>
  );
}
